import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { HuntPublic, MyHuntProgress } from '@cloudfruit-hunts/shared';
import { joinHunt } from '../../services/api';

interface HuntCardProps {
  hunt: HuntPublic;
  userProgress?: MyHuntProgress;
  isAuthenticated?: boolean;
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function HuntCard({ hunt, userProgress, isAuthenticated }: HuntCardProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isLocked = hunt.status === 'locked';
  const isScheduled = hunt.status === 'scheduled';
  const isLive = hunt.status === 'live';
  const isActive = isLive || isScheduled;

  const hasJoined = !!userProgress;
  const hasStarted = userProgress && userProgress.currentClueIndex > 0;
  const hasCompleted = !!userProgress?.completedAt;

  const joinMutation = useMutation({
    mutationFn: () => joinHunt(hunt.huntId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myHunts'] });
    },
  });

  const handleJoinClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    joinMutation.mutate();
  };

  const statusBadge = {
    locked: 'badge-locked',
    scheduled: 'bg-blue-100 text-blue-800',
    live: 'badge-live',
    won: 'badge-won',
    open: 'bg-gray-100 text-gray-700',
  }[hunt.status];

  const statusText = {
    locked: 'Locked',
    scheduled: 'Coming Soon',
    live: 'Live Now',
    won: 'Won',
    open: 'Open Season',
  }[hunt.status];

  const totalPrize = hunt.prizes.reduce((sum, p) => sum + p.amount, 0);
  const mainPrize = hunt.prizes.find(p => p.type === 'fastest');

  const cardContent = (
    <>
      <div className="flex items-start justify-between mb-3">
        <span className="text-sm font-bold text-primary">Hunt #{hunt.order || 1}</span>
        <span className={`badge ${statusBadge}`}>{statusText}</span>
      </div>

      <h3 className="text-lg font-bold text-text-dark mb-2">
        {isLocked ? (
          <span className="flex items-center gap-2">
            <LockIcon />
            {hunt.title}
          </span>
        ) : (
          hunt.title
        )}
      </h3>

      {/* Tags */}
      <div className="flex flex-wrap gap-2 mb-3">
        {hunt.themeTags.slice(0, 3).map(tag => (
          <span key={tag} className="text-xs bg-cream px-2 py-1 rounded text-text-light">
            {tag}
          </span>
        ))}
      </div>

      {/* Date info */}
      {isScheduled && hunt.launchAt && (
        <div className="text-sm text-text-light mb-3">
          <span className="font-medium">Starts:</span> {formatDate(hunt.launchAt)}
        </div>
      )}
      {isLive && hunt.openSeasonEndsAt && (
        <div className="text-sm text-text-light mb-3">
          <span className="font-medium">Prize deadline:</span> {formatDate(hunt.openSeasonEndsAt)}
        </div>
      )}

      {/* Difficulty and prizes */}
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-1" title={`Difficulty: ${hunt.difficulty}/5`}>
          {Array.from({ length: 5 }).map((_, i) => (
            <DifficultyDot key={i} filled={i < hunt.difficulty} />
          ))}
        </div>
        {isActive && totalPrize > 0 && (
          <span className="font-semibold text-accent">${totalPrize} in prizes</span>
        )}
      </div>

      {/* Prize breakdown for active hunts */}
      {isActive && mainPrize && !hunt.mainPrizeClaimed && (
        <div className="mt-3 pt-3 border-t border-gray-100 text-sm">
          <span className="text-secondary font-medium">${mainPrize.amount}</span>
          <span className="text-text-light"> for first solver</span>
        </div>
      )}

      {hunt.mainPrizeClaimed && (hunt.status === 'won' || hunt.status === 'open') && (
        <div className="mt-3 pt-3 border-t border-gray-100 text-sm text-accent">
          Main prize claimed!
        </div>
      )}

      {/* Clue count */}
      {hunt.clueCount && (
        <div className="mt-2 text-xs text-text-light">
          {hunt.clueCount} clues to solve
        </div>
      )}

      {/* Action button for joinable hunts */}
      {!isLocked && (isLive || hunt.status === 'open') && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          {hasCompleted ? (
            <span className="text-sm font-medium text-green-600">
              Completed!
            </span>
          ) : hasStarted ? (
            <span className="text-sm font-medium text-primary">
              In Progress ({userProgress.solvedCount}/{hunt.clueCount || '?'} clues)
            </span>
          ) : hasJoined ? (
            <span className="text-sm font-medium text-secondary">
              Ready to start
            </span>
          ) : (
            <button
              onClick={handleJoinClick}
              disabled={joinMutation.isPending}
              className="w-full py-2 px-4 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {joinMutation.isPending ? 'Joining...' : 'Join Hunt'}
            </button>
          )}
        </div>
      )}

      {/* Join button for scheduled hunts */}
      {isScheduled && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          {hasJoined ? (
            <span className="text-sm font-medium text-secondary">
              Joined - waiting for launch
            </span>
          ) : (
            <button
              onClick={handleJoinClick}
              disabled={joinMutation.isPending}
              className="w-full py-2 px-4 bg-secondary text-white rounded-lg font-medium hover:bg-secondary/90 disabled:opacity-50 transition-colors"
            >
              {joinMutation.isPending ? 'Joining...' : 'Join Waitlist'}
            </button>
          )}
        </div>
      )}
    </>
  );

  const className = `card block ${isLocked ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-lg'}`;

  if (isLocked) {
    return <div className={className}>{cardContent}</div>;
  }

  return (
    <Link to={`/h/${hunt.huntId}`} className={className}>
      {cardContent}
    </Link>
  );
}

function LockIcon() {
  return (
    <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function DifficultyDot({ filled }: { filled: boolean }) {
  return (
    <div
      className={`w-2 h-2 rounded-full ${
        filled ? 'bg-primary' : 'bg-gray-300'
      }`}
    />
  );
}
